import { useState, useEffect, useCallback, useRef } from "react";
import "@/App.css";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AuthPage from "@/pages/AuthPage";
import DiscoveryFeed from "@/components/DiscoveryFeed";
import CreateStory from "@/components/CreateStory";
import ProfilePage from "@/components/ProfilePage";
import EchoeDetailModal from "@/components/EchoeDetailModal";
import MyEchoes from "@/components/MyEchoes";
import MapPage from "@/components/MapPage";
import { useNaraApi } from "@/hooks/useNaraApi";
import RecordCard from "@/components/RecordCard";
import RecordDetail from "@/components/RecordDetail";
import SearchFilters from "@/components/SearchFilters";
import LoadingSkeleton from "@/components/LoadingSkeleton";
import { Search, Archive, Bookmark, Clock, ChevronRight, Loader2, X, PenLine, User, LogOut, Compass, Map as MapIcon } from "lucide-react";

const HERO_IMAGE = "https://images.unsplash.com/photo-1549964336-67d7d7d74ac2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwxfHxhcmNoaXZlJTIwbGlicmFyeXxlbnwwfHx8fDE3NzgwMzIzMjJ8MA&ixlib=rb-4.1.0&q=85&w=1920";

function AppContent() {
  const { user, loading: authLoading, logout } = useAuth();
  const api = useNaraApi();

  const [view, setView] = useState("feed"); // "feed", "search", "saved", "auth"
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [totalResults, setTotalResults] = useState(0);
  const [currentOffset, setCurrentOffset] = useState(0);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [selectedEchoe, setSelectedEchoe] = useState(null);
  const [savedIds, setSavedIds] = useState(new Set());
  const [showFilters, setShowFilters] = useState(false);
  const [showCreateStory, setShowCreateStory] = useState(false);
  const [filters, setFilters] = useState({ available_online: null, type_of_materials: "", result_types: "" });

  const resultsRef = useRef(null);
  const LIMIT = 20;

  // Load saved records when user is authenticated
  useEffect(() => {
    if (user) loadSavedRecords();
  }, [user]);

  // Redirect from auth view once logged in
  useEffect(() => {
    if (user && view === "auth") setView("feed");
  }, [user, view]);

  const loadSavedRecords = useCallback(async () => {
    if (!user) return;
    const records = await api.getSavedRecords();
    setSavedIds(new Set(records.map(r => r.naid)));
  }, [api, user]);

  const handleSearch = useCallback(async (searchQuery, offset = 0) => {
    const q = searchQuery !== undefined ? searchQuery : query;
    const data = await api.searchRecords({
      q: q.trim(), limit: LIMIT, offset,
      available_online: filters.available_online,
      type_of_materials: filters.type_of_materials,
      result_types: filters.result_types,
    });
    if (data) {
      setResults(data.records);
      setTotalResults(data.total);
      setCurrentOffset(offset);
      setView("search");
    }
  }, [query, filters, api]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    handleSearch(query, 0);
  };

  const handleSave = async (record) => {
    if (!user) { setView("auth"); return; }
    const success = await api.saveRecord(record);
    if (success) {
      setSavedIds(prev => new Set([...prev, record.naId || record.naid]));
    }
  };

  const handleUnsave = async (naid) => {
    if (!user) return;
    const success = await api.unsaveRecord(naid);
    if (success) {
      setSavedIds(prev => { const next = new Set(prev); next.delete(naid); return next; });
    }
  };

  const quickSearches = ["Declaration of Independence", "Civil War", "Apollo 11", "Census Records", "Immigration", "World War II"];

  // Loading state
  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#F9F7F1] flex items-center justify-center">
        <Loader2 size={32} className="animate-spin text-[#1A2B3C]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F7F1]">
      {/* Header */}
      <header className="bg-[#F9F7F1] border-b border-[#E5E2D9] sticky top-0 z-40" data-testid="app-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-3 cursor-pointer" onClick={() => { setView("feed"); setResults(null); }} data-testid="app-logo">
              <Archive size={20} className="text-[#1A2B3C]" />
              <span className="font-serif text-lg font-medium text-[#1A1A1A] tracking-tight">Echoes from the Past</span>
            </div>

            <nav className="flex items-center gap-1" data-testid="main-nav">
              <button
                className={`btn-ghost text-sm ${view === "feed" ? "tab-active" : ""}`}
                onClick={() => { setView("feed"); setResults(null); }}
                data-testid="nav-feed"
              >
                <Compass size={14} />
                <span className="hidden sm:inline">Discover</span>
              </button>
              <button
                className={`btn-ghost text-sm ${view === "map" ? "tab-active" : ""}`}
                onClick={() => setView("map")}
                data-testid="nav-map"
              >
                <MapIcon size={14} />
                <span className="hidden sm:inline">Map</span>
              </button>
              <button
                className={`btn-ghost text-sm ${view === "search" ? "tab-active" : ""}`}
                onClick={() => setView("search")}
                data-testid="nav-search"
              >
                <Search size={14} />
                <span className="hidden sm:inline">Archives</span>
              </button>
              {user && (
                <button
                  className={`btn-ghost text-sm ${view === "saved" ? "tab-active" : ""}`}
                  onClick={() => { setView("saved"); loadSavedRecords(); }}
                  data-testid="nav-saved"
                >
                  <Bookmark size={14} />
                  <span className="hidden sm:inline">Saved</span>
                </button>
              )}
              {user && user.role === "echo_maker" && (
                <button
                  className={`btn-ghost text-sm ${view === "my-echoes" ? "tab-active" : ""}`}
                  onClick={() => setView("my-echoes")}
                  data-testid="nav-my-echoes"
                >
                  <PenLine size={14} />
                  <span className="hidden sm:inline">My Echoes</span>
                </button>
              )}

              {/* User menu */}
              {user ? (
                <div className="flex items-center gap-2 ml-3 pl-3 border-l border-[#E5E2D9]">
                  {user.role === "echo_maker" && (
                    <button
                      className="btn-accent text-xs"
                      onClick={() => setShowCreateStory(true)}
                      data-testid="create-story-btn"
                    >
                      <PenLine size={12} />
                      <span className="hidden sm:inline">New Echoe</span>
                    </button>
                  )}
                  <button
                    className={`p-0.5 rounded-full transition-all hover:ring-2 hover:ring-[#B85C38] hover:ring-offset-1 ${view === "profile" ? "ring-2 ring-[#B85C38] ring-offset-1" : ""}`}
                    onClick={() => setView("profile")}
                    data-testid="nav-profile"
                    title="My Profile"
                  >
                    <div className="w-7 h-7 rounded-full bg-[#F4F1E6] border border-[#E5E2D9] flex items-center justify-center overflow-hidden">
                      {user.profile_picture ? (
                        <img
                          src={`${process.env.REACT_APP_BACKEND_URL}${user.profile_picture}`}
                          alt={user.name || "Profile"}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <User size={14} className="text-[#718096]" />
                      )}
                    </div>
                  </button>
                  <span className="font-mono text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-[#F4F1E6] border border-[#E5E2D9] text-[#718096]" data-testid="user-role">
                    {user.role === "echo_maker" ? "Echoe Maker" : "Echoe Hunter"}
                  </span>
                  <button className="btn-ghost p-1.5" onClick={logout} data-testid="logout-btn" title="Sign out">
                    <LogOut size={14} />
                  </button>
                </div>
              ) : (
                <button
                  className="btn-primary text-xs ml-3"
                  onClick={() => setView("auth")}
                  data-testid="login-btn"
                >
                  Sign In
                </button>
              )}
            </nav>
          </div>
        </div>
      </header>

      {/* Auth Page */}
      {view === "auth" && !user && <AuthPage />}

      {/* Discovery Feed */}
      {view === "feed" && (
        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-between mb-6">
            <h1 className="font-serif text-2xl sm:text-3xl font-medium text-[#1A1A1A]" data-testid="feed-heading">
              Discovery Feed
            </h1>
            <button
              className="btn-ghost text-xs"
              onClick={() => setView("map")}
              data-testid="feed-view-map"
              title="See stories on the map"
            >
              <MapIcon size={14} />
              <span>View on map</span>
            </button>
          </div>
          <DiscoveryFeed />
        </main>
      )}

      {/* Map view */}
      {view === "map" && <MapPage />}

      {/* Archive Search */}
      {view === "search" && (
        <div>
          {/* Hero when no results */}
          {!results && (
            <section className="hero-section" data-testid="hero-section">
              <div className="hero-bg" style={{ backgroundImage: `url(${HERO_IMAGE})` }} />
              <div className="hero-overlay" />
              <div className="hero-content animate-slide-up">
                <h1 className="font-serif text-4xl sm:text-5xl font-medium text-white mb-4 tracking-tight">
                  Explore the Archives
                </h1>
                <p className="text-base text-gray-300 mb-8 leading-relaxed max-w-lg mx-auto">
                  Search millions of records from the U.S. National Archives.
                </p>
                <form onSubmit={handleSearchSubmit} className="search-wrapper mb-6">
                  <Search size={18} className="search-icon" />
                  <input
                    type="text" value={query} onChange={e => setQuery(e.target.value)}
                    placeholder="Search the National Archives catalog..."
                    className="search-input pr-24" data-testid="hero-search-input"
                  />
                  <button type="submit" className="btn-accent absolute right-1 top-1/2 -translate-y-1/2" data-testid="hero-search-btn" disabled={api.loading}>
                    {api.loading ? <Loader2 size={16} className="animate-spin" /> : "Search"}
                  </button>
                </form>
                <div className="flex flex-wrap items-center justify-center gap-2">
                  <span className="font-mono text-xs uppercase tracking-widest text-gray-400">Try:</span>
                  {quickSearches.map(qs => (
                    <button key={qs} className="px-3 py-1.5 text-xs font-mono text-gray-300 border border-gray-500/40 hover:border-gray-300 hover:text-white transition-all"
                      onClick={() => { setQuery(qs); handleSearch(qs, 0); }}
                      data-testid={`quick-search-${qs.toLowerCase().replace(/\s+/g, "-")}`}
                    >{qs}</button>
                  ))}
                </div>
              </div>
            </section>
          )}

          {/* Results */}
          {results && (
            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" ref={resultsRef}>
              <form onSubmit={handleSearchSubmit} className="mb-6">
                <div className="search-wrapper">
                  <Search size={18} className="search-icon" />
                  <input type="text" value={query} onChange={e => setQuery(e.target.value)} placeholder="Search..." className="search-input pr-24" data-testid="results-search-input" />
                  <button type="submit" className="btn-accent absolute right-1 top-1/2 -translate-y-1/2" data-testid="results-search-btn" disabled={api.loading}>
                    {api.loading ? <Loader2 size={16} className="animate-spin" /> : "Search"}
                  </button>
                </div>
              </form>

              <div className="flex items-center justify-between mb-4">
                <span className="font-mono text-xs uppercase tracking-widest text-[#718096]" data-testid="results-count">
                  {totalResults} record{totalResults !== 1 ? "s" : ""} found
                </span>
                <button className={`btn-ghost text-xs ${showFilters ? "tab-active" : ""}`} onClick={() => setShowFilters(!showFilters)} data-testid="toggle-filters-btn">
                  {showFilters ? <X size={14} /> : <ChevronRight size={14} />} <span>Filters</span>
                </button>
              </div>

              {showFilters && (
                <div className="mb-6 p-4 border border-[#E5E2D9] bg-white animate-fade-in">
                  <SearchFilters filters={filters} onFilterChange={f => { setFilters(f); setTimeout(() => handleSearch(query, 0), 0); }} />
                </div>
              )}

              {api.loading ? <LoadingSkeleton count={4} /> : results.length === 0 ? (
                <div className="empty-state" data-testid="no-results">
                  <Archive size={48} className="text-[#E5E2D9] mb-4" />
                  <h3 className="font-serif text-xl text-[#718096]">No Records Found</h3>
                </div>
              ) : (
                <div className="space-y-3" data-testid="results-list">
                  {results.map((record, i) => (
                    <RecordCard key={record.naId || i} record={record} index={i}
                      onClick={setSelectedRecord} onSave={handleSave} onUnsave={handleUnsave}
                      isSaved={savedIds.has(record.naId || record.naid)} />
                  ))}
                </div>
              )}

              {results && results.length > 0 && (
                <div className="flex items-center justify-between mt-8 pt-6 border-t border-[#E5E2D9]" data-testid="pagination">
                  <button className="btn-secondary" onClick={() => handleSearch(query, Math.max(0, currentOffset - LIMIT))} disabled={currentOffset === 0} style={{ opacity: currentOffset === 0 ? 0.4 : 1 }}>Previous</button>
                  <span className="font-mono text-xs text-[#718096]">
                    {currentOffset + 1} - {Math.min(currentOffset + LIMIT, totalResults)} of {totalResults}
                  </span>
                  <button className="btn-secondary" onClick={() => handleSearch(query, currentOffset + LIMIT)} disabled={currentOffset + LIMIT >= totalResults} style={{ opacity: currentOffset + LIMIT >= totalResults ? 0.4 : 1 }}>Next</button>
                </div>
              )}
            </main>
          )}
        </div>
      )}

      {/* Saved tab placeholder */}
      {view === "saved" && user && (
        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <h2 className="font-serif text-2xl font-medium text-[#1A1A1A] mb-6" data-testid="saved-heading">Saved Records</h2>
          <p className="text-sm text-[#718096] mb-4">Records and echoes you've bookmarked.</p>
          <SavedRecordsList savedIds={savedIds} onUnsave={handleUnsave} onSelectRecord={setSelectedRecord} onSelectEchoe={setSelectedEchoe} />
        </main>
      )}

      {/* Profile page */}
      {view === "profile" && user && (
        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <ProfilePage />
        </main>
      )}

      {/* My Echoes view */}
      {view === "my-echoes" && user && (
        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <MyEchoes onOpen={setSelectedEchoe} />
        </main>
      )}

      {/* Record Detail Modal (NARA records) */}
      {selectedRecord && (
        <RecordDetail record={selectedRecord} onClose={() => setSelectedRecord(null)}
          onSave={handleSave} onUnsave={handleUnsave}
          isSaved={savedIds.has(selectedRecord.naId || selectedRecord.naid)} />
      )}

      {/* Echoe Detail Modal (stories) */}
      {selectedEchoe && (
        <EchoeDetailModal
          story={selectedEchoe}
          onClose={() => setSelectedEchoe(null)}
          canFavorite={!!user}
          canBookmark={!!user}
        />
      )}

      {/* Create Story Modal */}
      {showCreateStory && (
        <CreateStory onCreated={() => setView("feed")} onClose={() => setShowCreateStory(false)} />
      )}

      {/* Footer */}
      <footer className="border-t border-[#E5E2D9] mt-12 py-6 bg-[#F9F7F1]" data-testid="app-footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Archive size={14} className="text-[#718096]" />
            <span className="font-mono text-xs text-[#718096] uppercase tracking-widest">Echoes from the Past</span>
          </div>
          <p className="text-xs text-[#718096]">
            Powered by the <a href="https://www.archives.gov/developer" target="_blank" rel="noopener noreferrer" className="text-[#B85C38] hover:underline">U.S. National Archives Catalog API</a>
          </p>
        </div>
      </footer>
    </div>
  );
}

// Saved Records List sub-component
function SavedRecordsList({ savedIds, onUnsave, onSelectRecord, onSelectEchoe }) {
  const api = useNaraApi();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const data = await api.getSavedRecords();
      setRecords(data);
      setLoading(false);
    })();
  }, [savedIds]);

  if (loading) return <LoadingSkeleton count={3} />;
  if (records.length === 0) return (
    <div className="empty-state" data-testid="saved-empty">
      <Bookmark size={40} className="text-[#E5E2D9] mb-4" />
      <h3 className="font-serif text-xl text-[#718096]">No Saved Records</h3>
      <p className="text-sm text-[#718096]">Bookmark echoes from the feed or records from Archives.</p>
    </div>
  );

  const handleClick = (r) => {
    if (r.naid && r.naid.startsWith("story_") && r.raw_data) {
      // It's a saved echoe - open with EchoeDetailModal
      onSelectEchoe?.(r.raw_data);
    } else {
      // It's a NARA record - open with RecordDetail
      onSelectRecord?.({ naId: r.naid, title: r.title, description: r.description, recordType: r.record_type, dateRange: r.date_range, thumbnailUrl: r.thumbnail_url, hasOnlineContent: r.has_online_content, ...(r.raw_data || {}) });
    }
  };

  return (
    <div className="space-y-3" data-testid="saved-records-list">
      {records.map((r, i) => {
        const isEchoe = r.naid && r.naid.startsWith("story_");
        return (
          <div key={r.naid} className={`record-card p-5 animate-fade-in stagger-${(i % 8) + 1}`} data-testid={`saved-record-${r.naid}`}>
            <div className="flex items-start justify-between">
              <div className="cursor-pointer flex-1" onClick={() => handleClick(r)}>
                <div className="flex items-center gap-2 mb-1.5">
                  {isEchoe && r.record_type && (
                    <span className="type-badge text-[10px]">{r.record_type}</span>
                  )}
                  <span className="font-mono text-[10px] uppercase tracking-wider text-[#718096]">
                    {isEchoe ? "Echoe" : "Archive Record"}
                  </span>
                </div>
                <h4 className="font-serif text-base font-medium text-[#1A1A1A] mb-1 hover:text-[#B85C38] transition-colors">{r.title}</h4>
                {r.description && (
                  <p className="text-xs text-[#4A5568] line-clamp-2 mb-1.5">{r.description}</p>
                )}
                <div className="flex gap-3 font-mono text-xs text-[#718096]">
                  {r.date_range && <span>{r.date_range}</span>}
                  {!isEchoe && <span>NAID: {r.naid}</span>}
                </div>
              </div>
              <button className="btn-ghost p-2 text-red-500 hover:text-red-700" onClick={() => onUnsave(r.naid)} data-testid={`remove-saved-${r.naid}`}>
                <X size={14} />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
